import { useEffect, useRef, useState } from 'react';
import businessValueImage from '../../../assets/images/giatrithuctedevsecops.jpg';

interface BenefitItem {
  id: string;
  title: string;
  description: string;
  image?: string;
  order?: number;
}

interface BusinessValueSectionProps {
  items?: BenefitItem[];
  loading?: boolean;
}

export default function BusinessValueSection({ items = [], loading = false }: BusinessValueSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Tách section image item và benefits items từ props
  const sectionImageItem = items.find((item: BenefitItem & { order?: number }) => 
    item.title === '_SECTION_IMAGE' || (item.order !== undefined && item.order < 0)
  );
  const benefitsItems = items.filter((item: BenefitItem & { order?: number }) => 
    item.title !== '_SECTION_IMAGE' && (item.order === undefined || item.order >= 0)
  );

  // Lấy description và image từ section image item hoặc dùng giá trị mặc định
  const sectionTitle = 'Giá trị thực tế cho doanh nghiệp';
  const sectionSubtitle = sectionImageItem?.description || 'DevSecOps không chỉ là công nghệ, mà là lợi thế cạnh tranh trong kỷ nguyên số';
  const sectionImage = sectionImageItem?.image || businessValueImage;

  // Fallback hardcoded data
  const defaultBenefits = [
    {
      id: 'default-1',
      title: 'Rút ngắn thời gian release sản phẩm',
      description: 'Từ vài tuần xuống còn vài giờ. Nhanh chóng đưa tính năng mới đến người dùng, tăng khả năng cạnh tranh.'
    },
    {
      id: 'default-2',
      title: 'Phát hiện sớm rủi ro bảo mật và lỗi kỹ thuật',
      description: 'Chi phí sửa lỗi giảm 90% khi phát hiện ở giai đoạn development thay vì production.'
    },
    {
      id: 'default-3',
      title: 'Nâng cao chất lượng mã nguồn và độ ổn định hệ thống',
      description: 'Automated testing và code review đảm bảo chất lượng đồng nhất, giảm downtime, tăng độ tin cậy.'
    },
    {
      id: 'default-4',
      title: 'Sẵn sàng mở rộng trong chiến lược chuyển đổi số',
      description: 'Nền tảng DevSecOps là tiền đề để triển khai Cloud, Microservices, AI/ML trong tương lai.'
    }
  ];

  const benefits = benefitsItems.length > 0 ? benefitsItems : defaultBenefits;

  return (
    <section ref={sectionRef} id="value" className="w-full overflow-x-hidden pt-8 md:pt-12 lg:pt-20 pb-8 md:pb-12 lg:pb-20 bg-[#E8F5E9]/12">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 lg:gap-16 items-center">
          {/* Left Column - Header & Visual */}
          <div className={`lg:col-span-2 space-y-6 md:space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div>
              <div className="inline-block mb-3 md:mb-4">
                <span className="text-xs md:text-sm font-semibold text-[#00b587] uppercase tracking-wider">
                  GIÁ TRỊ THỰC TẾ
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2a4b7c] leading-tight mb-4 md:mb-5">
                {sectionTitle}
              </h2>
              <p className="text-sm md:text-base text-[#64748B] leading-relaxed">
                {sectionSubtitle}
              </p>
            </div>

            {/* Visual */}
            <div className="relative">
              <div className="rounded-xl md:rounded-2xl overflow-hidden">
                <img 
                  src={sectionImage}
                  alt="Business Value Visualization"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Benefits List */}
          <div className={`lg:col-span-3 space-y-6 md:space-y-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-16 h-16 border-4 border-[#00b587] border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Đang tải...</p>
              </div>
            ) : benefits.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Chưa có nội dung</p>
              </div>
            ) : (
              <>
                {benefits.map((benefit, index) => (
              <div 
                key={index}
                className={`flex items-start gap-4 md:gap-5 group cursor-pointer transition-all duration-1000 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
                style={{ transitionDelay: isVisible ? `${0.4 + index * 0.1}s` : '0s' }}
              >
                {/* Check Icon */}
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#00b587]/15 flex items-center justify-center group-hover:bg-[#00b587]/25 transition-colors duration-300">
                  <i className="ri-checkbox-circle-fill text-xl md:text-2xl text-[#00b587]"></i>
                </div>

                {/* Content */}
                <div className="flex-1 pt-1">
                  <h3 className="text-lg md:text-xl font-semibold text-[#2a4b7c] mb-2 leading-snug">
                    {benefit.title}
                  </h3>
                  <p className="text-sm md:text-base text-[#64748B] leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
